import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useReportsData } from '../../hooks/useReportsData';
import { FileBarChart, Briefcase, Gavel, AlertTriangle, CheckCircle, Download, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

export const ExecutiveReport = () => {
    const [searchParams] = useSearchParams();
    const filters = {
        client: searchParams.get('client'),
        period: searchParams.get('period'),
        risk: searchParams.get('risk')
    };

    const { trademarks, alerts, procedures, loading } = useReportsData(filters);

    const kpis = [
        { label: 'Total Marcas', value: trademarks.length, icon: Briefcase, color: 'text-blue-600', bg: 'bg-blue-50' },
        { label: 'Registradas', value: trademarks.filter(t => t.status === 'Registrada').length, icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50' },
        { label: 'En Trámite', value: trademarks.filter(t => t.status === 'En trámite').length, icon: FileBarChart, color: 'text-indigo-600', bg: 'bg-indigo-50' },
        { label: 'Riesgo Alto', value: trademarks.filter(t => t.risk === 'high').length, icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50' },
    ];

    const handleExport = (format) => {
        toast.info(`Generando reporte ${format}... (Fase 2)`);
    };

    if (loading) {
        return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-gray-400" /></div>;
    }

    return (
        <div className="space-y-6 animate-in fade-in duration-500">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h2 className="text-xl font-bold text-gray-900">Resumen Ejecutivo</h2>
                    <p className="text-sm text-gray-500">Estado general del portafolio y riesgos detectados</p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => handleExport('Excel')}>Exportar Excel</Button>
                    <Button size="sm" onClick={() => handleExport('PDF')}><Download className="mr-2 h-4 w-4" /> PDF Ejecutivo</Button>
                </div>
            </div>

            {/* KPIS */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {kpis.map((kpi, idx) => (
                    <Card key={idx} className="border-gray-200">
                        <CardContent className="p-4 flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-500">{kpi.label}</p>
                                <p className="text-2xl font-bold text-gray-900 mt-1">{kpi.value}</p>
                            </div>
                            <div className={`p-3 rounded-full ${kpi.bg}`}>
                                <kpi.icon className={`h-5 w-5 ${kpi.color}`} />
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            {/* Main Grid: Insights & Charts Placeholders */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Insights / Status */}
                <Card className="lg:col-span-2 border-gray-200">
                    <CardHeader>
                        <CardTitle>Estado del Portafolio</CardTitle>
                        <CardDescription>Distribución por estatus legal</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="h-64 bg-gray-50 rounded flex items-center justify-center text-gray-400 text-sm border border-dashed border-gray-200">
                            Chart Placeholder: Pie Chart (Registradas vs Trámite vs Litigio)
                        </div>
                    </CardContent>
                </Card>

                {/* Alerts & Risks */}
                <Card className="border-gray-200">
                    <CardHeader>
                        <CardTitle>Alertas Recientes</CardTitle>
                        <CardDescription>Monitoreo de Gaceta</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {alerts.length === 0 ? (
                            <p className="text-sm text-gray-500 text-center py-4">Sin alertas recientes.</p>
                        ) : (
                            alerts.slice(0, 5).map(alert => (
                                <div key={alert.id} className="flex items-start gap-3 p-3 bg-red-50 rounded-lg border border-red-100">
                                    <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5" />
                                    <div>
                                        <p className="text-sm font-semibold text-red-900">Coincidencia: {alert.detected}</p>
                                        <p className="text-xs text-red-700">vs {alert.watched} ({alert.similarity})</p>
                                    </div>
                                </div>
                            ))
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Actions */}
            <Card className="border-gray-200">
                <CardHeader>
                    <CardTitle>Próximas Acciones Requeridas</CardTitle>
                    <CardDescription>Basado en vencimientos y requerimientos oficiales</CardDescription>
                </CardHeader>
                <CardContent>
                    {trademarks.filter(t => t.nextAction !== 'N/A').length === 0 ? (
                        <p className="text-sm text-gray-500">No hay acciones pendientes.</p>
                    ) : (
                        <div className="space-y-2">
                            {trademarks
                                .filter(t => t.nextAction !== 'N/A')
                                .slice(0, 5)
                                .map((tm) => (
                                    <div key={tm.id} className="flex items-center justify-between p-3 border-b border-gray-100 last:border-0">
                                        <div className="flex items-center gap-3">
                                            <Gavel className="h-4 w-4 text-gray-400" />
                                            <div>
                                                <p className="text-sm font-medium text-gray-900">{tm.nextAction}</p>
                                                <p className="text-xs text-gray-500">Marca: {tm.name} • Vence: {tm.nextActionDate}</p>
                                            </div>
                                        </div>
                                        <Button variant="ghost" size="sm" className="text-indigo-600">Ver</Button>
                                    </div>
                                ))}
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};
